<template>
  <v-container style="display: flex; flex-direction: column; height: calc(-50px + 100vh); width: 100%; overflow: hidden;">
    <div style="display: flex; flex-direction: row; gap: 8px">
      <v-btn icon @click="$emit('navigate',undefined)">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <p class="v-card-title">{{ title }}</p>
    </div>  
    <div style="width: 100%; display: flex; justify-content: center;">
      <GraphlyD3
        ref="graphly"
        :dark="darkTheme"
        :env-gravity="0"
        style="width: 180px; height: 150px"
      />
    </div>    
    <div style="width: 100%; overflow-x: scroll; display: flex; gap: 8px;">
      <sub-node-graph 
        v-for="node in subnodes"
        :key="node.id"
        :node="node"
        @navigate="$emit('navigate',node.id)" 
      />
    </div>
    <hr>
    <v-container style="overflow-y: auto; flex: 1; padding-bottom: 150px">
      <div
        v-for="child in bubbles[0]?.children"
        style="display: contents;"
      >
        <object-card v-if="child.shape != undefined" :bubbleItem="child" />
        <nested-card v-else-if="child?.children?.length != 0" :nested="child" @navigate="$event => $emit('navigateSub', $event)" />
        <data-card v-else :bubbleItem="{...child, path:[bubbles[0].name]}" @render="render" />
      </div>
    </v-container>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted, watch } from "vue";
import "@livereader/graphly-d3/style.css";
import GraphlyD3 from "@livereader/graphly-d3/component/vue3";
import { useGraphStore } from "../../../../store/graph";
import { useSymbolsStore } from "../../../../store/symbols";
import { ConceptDataobject, InstanceDataobject } from "@lr-notitia/notitia-operator/schemas";
import SubNodeGraph from "./SubNodeGraph.vue";
import { useDomainStore } from "../../../../store/domain";
import { useWorkerStore } from "../../../../store/worker";
import { Bubble } from '../ComposedAttributesModal.vue';
import "@lr-notitia/bubbly-d3/style.css";
import DataCard from "./DataCard.vue";
import NestedCard from "./NestedCard.vue";
import ObjectCard from "./ObjectCard.vue";

const compProps = defineProps<{
	nodes: Array<any>;
  links: Array<any>;
  dataobjectId: string;
	isNested: boolean;
	parentId: string;
}>();
defineEmits(["navigate", "navigateSub"]);

watch(compProps, () => {
  load()
})

const graphly = ref<any>(null);
const darkTheme = ref(false);
const bubbles = ref<Bubble[]>([]);

const graph = computed<{nodes:Array<any>, links: Array<any>}>( () => (
  { nodes:[activeNode.value], links: []}
))
const activeNode = computed(() => {
  return compProps.nodes.find((n:any) => n.id == compProps.dataobjectId)
})
const subnodes = computed(() => {
  let sub = compProps.links.filter((l) => l.source == compProps.dataobjectId).map(n => n.target)
  return compProps.nodes.filter(n => sub.includes(n.id) && n.id != compProps.dataobjectId)
})

const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();
const domainStore = useDomainStore();
const workerStore = useWorkerStore();
let worker = ref<any>(null);

const dataobject = computed(() => {
	if (false) return graphStore.concept.dataobject.byId(compProps.dataobjectId);
	else return graphStore.instance.dataobject.byId(compProps.dataobjectId);
});

const title = computed(() => {
	if (!dataobject.value) return "";
	if (false) {
		return symbolsStore.term({
			id: (dataobject.value as ConceptDataobject).refersTo,
			terminology: (dataobject.value as ConceptDataobject).terminology,
		});
	} else {
		let concept = graphStore.concept.dataobject.byId((dataobject.value as InstanceDataobject).concept);
		if (!concept) return "";
		return symbolsStore.term({ id: concept.refersTo, terminology: concept.terminology });
	}
});

onMounted(async () => {
	await load()
});
onUnmounted(() => {
	worker.value.terminate();
	workerStore.releaseWorker("bubbly");
});

async function load() {
  graphly.value.simulation.render(graph.value, 0);
  graphly.value.simulation.moveTo({
		nodes: graph.value.nodes,
		padding: 10,
	});

  worker.value = await workerStore.spawn("bubbly");
	worker.value.addEventListener("message", (event: any) => {
		bubbles.value = event.data.result;
	});
	render();
}

function postWorker() {
	worker.value.postMessage({
		graph: JSON.parse(JSON.stringify(graphStore.graph)),
		symbols: JSON.parse(JSON.stringify(symbolsStore.symbols)),
		dataobject: JSON.parse(JSON.stringify(dataobject.value)),
		isNested: compProps.isNested,
		languagePreference: domainStore.languagePreference,
		parentId: compProps.parentId,
		role: domainStore.account.role,
	});
}

function render() {
	postWorker();
}
</script>